import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { NotificationPreferencesService } from '../../services/notification-preferences/notification-preferences.service';
import { AlertError, AlertSuccess } from '../actions/shared.actions';
import {
    DeleteEmailPreferences,
    DeleteEmailPreferencesSuccess,
    GetEmailPreferences,
    GetUnsubscribeDetails,
    SetEmailPreferences,
    SetError,
    SetLoading,
    SetUnsubscribeDetails,
    UnsubscribeEmail,
    UnsubscribeEmailSuccess,
} from '../actions/notification-preferences.actions';

@Injectable()
export class NotificationPreferencesEffects {
    constructor(private actions$: Actions, private notificationPreferencesService: NotificationPreferencesService) {}

    getUnsubscribeDetails$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetUnsubscribeDetails),
            mergeMap(action => {
                return from(this.notificationPreferencesService.getUnsubscribeDetails(action.token)).pipe(
                    map(unsubscribeDetails => {
                        return SetUnsubscribeDetails({ unsubscribeDetails });
                    }),
                    catchError(error => {
                        return of(SetError({ error: error?.msg || 'Failed to fetch unsubscribe details' }));
                    })
                );
            })
        );
    });

    unsubscribeEmail$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UnsubscribeEmail),
            mergeMap(action => {
                return from(
                    this.notificationPreferencesService.unsubscribeByEmail({
                        token: action.token,
                        email: action.email,
                        preference: action.preference as any,
                    })
                ).pipe(
                    map(() => {
                        return UnsubscribeEmailSuccess();
                    }),
                    catchError(error => {
                        return of(SetError({ error: error?.msg || 'Failed to unsubscribe' }));
                    })
                );
            })
        );
    });

    getEmailPreferences$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetEmailPreferences),
            mergeMap(() => {
                return from(this.notificationPreferencesService.getEmailPreferences()).pipe(
                    map(emailPreferences => {
                        return SetEmailPreferences({ emailPreferences });
                    }),
                    catchError(error => {
                        return of(SetError({ error: error?.msg || 'Failed to fetch email preferences' }));
                    })
                );
            })
        );
    });

    deleteEmailPreferences$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteEmailPreferences),
            mergeMap(action => {
                return from(this.notificationPreferencesService.deleteEmailPreferences(action.preferences)).pipe(
                    map(() => {
                        return DeleteEmailPreferencesSuccess();
                    }),
                    catchError(error => {
                        return of(SetError({ error: error?.msg || 'Failed to delete email preferences' }));
                    })
                );
            })
        );
    });

    setLoading$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GetUnsubscribeDetails, UnsubscribeEmail, GetEmailPreferences, DeleteEmailPreferences),
            map(() => SetLoading({ loading: true }))
        );
    });

    clearLoading$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetUnsubscribeDetails, UnsubscribeEmailSuccess, SetEmailPreferences, DeleteEmailPreferencesSuccess, SetError),
            map(() => SetLoading({ loading: false }))
        );
    });

    showError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetError),
            map(action => {
                if (action.error) {
                    return AlertError({ message: action.error });
                }
                return { type: 'NO_ACTION' };
            })
        );
    });

    showSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UnsubscribeEmailSuccess),
            map(() => AlertSuccess({ message: 'Successfully unsubscribed!' }))
        );
    });

    showDeleteSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteEmailPreferencesSuccess),
            map(() => AlertSuccess({ message: 'Successfully deleted preferences!' }))
        );
    });
}
